import { debounce } from 'lodash';
import { initNumberField } from '@general/helpers';
import { changeCart, addItemToCart, updateCart, afterCartChangeType } from '@services/cart';
import { closeModal, openModal } from './modals';

// Store listener reference for cleanup
let cartEventListenerAttached = false;

/**
 * Finds the minicart element within the specified root element.
 *
 * @param {Document|HTMLElement} [root=document] - The root element to search within.
 * @returns {HTMLElement|null} The minicart element if found, otherwise null.
 */
const findMinicart = (root = document) => root.querySelector('#MinicartDrawer');

/**
 * Adds customization label to the first element's title if customization exists
 *
 * @param {HTMLElement} groupWrapper - The group wrapper element
 */
const addCustomizationLabel = (groupWrapper) => {
  const customizationAdded = groupWrapper.querySelector('.cart-line__wrapper[data-customization="true"]');
  if (customizationAdded) {
    const title = groupWrapper.querySelector('.cart-line__row--title');
    const customizationText = groupWrapper.querySelector('.cart-line__customization-text');
    if (title && !customizationText) {
      const customizationText = document.createElement('p');
      customizationText.classList.add('cart-line__customization-text');
      customizationText.innerText = customizationAdded.dataset.customizationAdded;
      title.insertAdjacentElement('afterend', customizationText);
    }
  }
};

/**
 * Removes orphan customization items (data-customization="true" and data-group="false")
 * from the given container and syncs the removal with the cart API when possible.
 *
 * @param {HTMLElement} container - Minicart or cart wrapper containing line items.
 */
const removeOrphanCustomizations = (container) => {
  if (!container) return;

  // Select all customization lines that are not correctly grouped
  const orphans = container.querySelectorAll('.cart-line__wrapper[data-customization="true"][data-grouped="false"]');

  if (!orphans.length) return;

  const updates = {};

  // Collect all orphan items by key to set their quantity to 0
  orphans.forEach((orphan) => {
    const key = orphan.dataset.key;
    if (key) {
      updates[key] = 0;
    }
  });

  // If there is at least one valid key, send a batch update to the cart API
  if (Object.keys(updates).length > 0 && typeof updateCart === 'function') {
    updateCart({
      items: updates,
      callbacks: {
        // No specific UI handling here, minicart will be refreshed by normal flow
        beforeFetch: () => {},
        afterFetch: () => {},
      },
    }).catch(() => {
      // In case of API error, keep going with DOM cleanup to avoid broken UI
    });
  }

  // Remove orphan DOM nodes from the UI
  orphans.forEach((orphan) => orphan.remove());

  // Remove empty group wrappers if any
  container.querySelectorAll('.cart-line__products-group').forEach((group) => {
    if (!group.querySelector('.cart-line__wrapper')) {
      group.remove();
    }
  });
};

/**
 * Initializes customization grouping for cart items with the same group ID
 *
 * @param {HTMLElement} container - The container element to search for grouped items
 */
const initCustomization = (container) => {
  if (!container) return;

  const items = container.querySelectorAll('.cart-line__wrapper');
  items.forEach((item) => {
    const groupIp = item.dataset.group;
    if (groupIp) {
      const selector = `.cart-line__wrapper[data-grouped="false"][data-group="${groupIp}"]`;
      const groupedProducts = Array.from(container.querySelectorAll(selector));
      if (groupedProducts.length > 0) {
        const groupWrapper = document.createElement('div');
        groupWrapper.classList.add('cart-line__products-group');

        // Insert wrapper before the first group element
        const first = groupedProducts[0];
        first.parentNode.insertBefore(groupWrapper, first);

        // Move all elements into the wrapper
        groupedProducts.forEach((el) => {
          groupWrapper.appendChild(el);
        });

        // Set data-grouped flag to true for all group elements
        groupedProducts.forEach((el) => {
          el.setAttribute('data-grouped', 'true');
        });

        // Append customization text to the first element's title if customization exists
        if (groupWrapper.children.length > 1) {
          addCustomizationLabel(groupWrapper);
        }
      } else {
        // maybe `cart-line__products-group` already exists
        const groupWrapper = item.closest('.cart-line__products-group');
        if (groupWrapper) {
          addCustomizationLabel(groupWrapper);
        }
      }
    }
  });
};

/**
 * Initializes giftwrap grouping for cart items
 * Handles legacy data-group attribute for giftwrap products
 *
 * @param {HTMLElement} container - The minicart container element
 */
const initGiftwrapGrouping = (container) => {
  if (!container) return;

  const productRows = container.querySelectorAll('.cart-line__wrapper[data-group]');
  const grouped = new Set();

  productRows.forEach((row) => {
    const groupId = row.getAttribute('data-group');
    if (groupId && !grouped.has(groupId)) {
      const groupRows = Array.from(
        container.querySelectorAll(`.cart-line__wrapper[data-group="${groupId}"]:not([data-grouped="true"])`),
      );
      if (groupRows.length > 0) {
        const groupDiv = document.createElement('div');
        groupDiv.classList.add('cart-line__products-group');
        groupRows[0].parentNode.insertBefore(groupDiv, groupRows[0]);
        groupRows.forEach((grRow) => {
          groupDiv.appendChild(grRow);
          grRow.setAttribute('data-grouped', 'true');
        });
      }
      grouped.add(groupId);
    }
  });

  // Handle giftwrap opener visibility
  container.querySelectorAll('.cart-line__products-group').forEach((group) => {
    const hasGiftwrap = group.querySelector('.cart-line__wrapper[data-giftwrap="true"]');
    const opener = group.querySelector('.cart-line__giftwrap');
    if (opener) {
      if (!hasGiftwrap) {
        opener.classList.add('visible');
      } else {
        opener.classList.remove('visible');
      }
    }
  });
};

/**
 * Initializes the minicart by setting up event listeners and initializing line items.
 */
const initMinicart = () => {
  const minicart = findMinicart();
  if (!minicart) return;

  // Initialize giftwrap grouping (legacy)
  initGiftwrapGrouping(minicart);

  const privacyCheckbox = minicart.querySelector('.privacy-checkbox__container');
  if (privacyCheckbox && typeof window.dAgency.initPrivacyCheckbox == 'function')
    window.dAgency.initPrivacyCheckbox(privacyCheckbox);

  minicart
    .querySelector('.minicart__continue-shopping')
    ?.addEventListener('click', () => closeModal(minicart));

  minicart.querySelectorAll('.cart-line__wrapper[data-key]').forEach((line) =>
    initLine({
      line,
      beforeFetch: () => {
        minicart.classList.add('loading');
      },
      afterFetch: () => {
        updateMinicart();
      },
    }),
  );

  // Initialize customization grouping
  initCustomization(minicart);

  // Remove any orphan customization items after grouping has been applied
  removeOrphanCustomizations(minicart);
};

/**
 * Updates the minicart content by fetching the latest data and reinitializing the minicart.
 */
const updateMinicart = async () => {
  const minicart = findMinicart();
  if (!minicart) return;
  minicart.classList.add('loading');
  await fetch(minicart.dataset.refreshUrl)
    .then((res) => res.text())
    .then((data) => {
      const minicartSection = new DOMParser().parseFromString(data, 'text/html');
      const fetchMinicart = findMinicart(minicartSection);
      if (fetchMinicart) minicart.innerHTML = fetchMinicart.innerHTML;
      minicart
        .querySelectorAll('.modal__backdrop , .modal__close')
        .forEach((closer) => closer.addEventListener('click', () => closeModal(minicart)));
      initMinicart();
    });
  minicart.classList.remove('loading');
};

/**
 * Initializes a line item in the minicart by setting up event listeners for
 * quantity changes and item removal.
 * @param {Object} options - Options for initializing the line item.
 * @param {HTMLElement} options.line - The line item element.
 * @param {Function} [options.beforeFetch] - Function to call before sending the request.
 * @param {Function} [options.afterFetch] - Function to call after successfully changing the cart item.
 */
const initLine = ({ line, beforeFetch, afterFetch }) => {
  if (!line) return;
  const minusBtn = line.querySelector('.cart-line__quantity-btn--minus');
  const plusBtn = line.querySelector('.cart-line__quantity-btn--plus');
  const field = line.querySelector('.cart-line__quantity-value');

  if (minusBtn && plusBtn && field) {
    initNumberField({
      plusBtn,
      minusBtn,
      field,
    });

    field.addEventListener(
      'change',
      debounce(() => {
        changeCart({
          key: line.dataset.key,
          quantity: parseInt(field.value),
          callbacks: {
            beforeFetch: beforeFetch,
            afterFetch: afterFetch,
          },
        });
      }, 300),
    );
  }

  const remove = line.querySelector('.cart-line__remove');
  if (remove) {
    remove.addEventListener('click', async (e) => {
      e.preventDefault();

      // Check for customization and grouped flags
      const isCustomization = line.dataset.customization === 'true';
      const isGrouped = line.dataset.grouped === 'true';

      if (isCustomization) {
        // Remove customization line and update minicart
        await changeCart({
          key: line.dataset.key,
          quantity: 0,
          callbacks: {
            beforeFetch: beforeFetch,
            afterFetch: afterFetch,
          },
        });
      } else if (isGrouped) {
        const groupIp = line.dataset.group;
        // FIX: Search for grouped products globally in the minicart/cart container
        // This ensures we catch them even if they are not visually grouped in a wrapper
        const container = line.closest('.minicart__wrapper') || line.closest('.cart__wrapper') || document.body;
        const groupedProducts = Array.from(container.querySelectorAll(`.cart-line__wrapper[data-group="${groupIp}"]`));

        // Batch update to remove all related items
        const updates = {};
        groupedProducts.forEach((product) => {
          updates[product.dataset.key] = 0;
        });

        // Single API call for all products in group
        await updateCart({
          items: updates,
          callbacks: {
            beforeFetch: beforeFetch,
            afterFetch: afterFetch,
          },
        });

        // Remove products from DOM after successful update
        groupedProducts.forEach((product) => {
          product.remove();
        });

        // Cleanup empty wrappers if they exist
        const productsWrapper = line.closest('.cart-line__products-group');
        if (productsWrapper && productsWrapper.querySelectorAll('.cart-line__wrapper').length === 0) {
          productsWrapper.remove();
        }
      } else {
        // Handle legacy giftwrap grouping removal with batch update
        const group = line.closest('.cart-line__products-group');
        const itemKey = line.dataset.key;
        let updates = { [itemKey]: 0 };

        if (!remove.classList.contains('giftwrap')) {
          const customization = group?.querySelector('.cart-line__wrapper[data-customization="true"]');
          if (customization) updates[customization.dataset.key] = 0;
          const giftwrap = group?.querySelector('.cart-line__wrapper[data-giftwrap="true"]');
          if (giftwrap) updates[giftwrap.dataset.key] = 0;
        } else {
          const opener = group?.querySelector('.giftwrap-opener');
          if (opener) opener.classList.add('visible');
        }

        // Use updateCart for batch removal
        await updateCart({
          items: updates,
          callbacks: {
            beforeFetch,
            afterFetch,
          },
        });
      }
    });
  }

  // Initialize giftwrap drawer functionality
  const giftwrapOpener = line.querySelector('.giftwrap-opener');
  if (giftwrapOpener) {
    giftwrapOpener.addEventListener('click', () => {
      const groupId = line.getAttribute('data-group');
      const drawer = document.querySelector(`#gift__drawer--${groupId}`);
      if (drawer) {
        drawer.classList.add('open');

        const closeBtn = drawer.querySelector('.gift__drawer__close');
        if (closeBtn) {
          closeBtn.addEventListener('click', () => {
            drawer.classList.remove('open');
          });
        }

        const products = drawer.querySelectorAll('.gift__drawer__content__product');
        const message = drawer.querySelector('.gift__drawer__content__message');
        const actions = drawer.querySelector('.gift__drawer__footer');
        const button = actions ? actions.querySelector('button[data-group]') : null;

        products.forEach((product) => {
          product.addEventListener('click', () => {
            products.forEach((p) => p.classList.remove('selected'));
            product.classList.add('selected');
            if (message) message.classList.add('open');
            if (button) {
              button.removeAttribute('disabled');
              button.setAttribute('data-item', product.getAttribute('data-item'));
            }
          });
        });

        if (button) {
          button.addEventListener('click', () => {
            const variantId = button.getAttribute('data-item');
            const giftMessage = drawer.querySelector('textarea[name="gift-message"]')?.value || '';
            if (variantId) {
              addItemToCart({
                id: variantId,
                quantity: 1,
                properties: {
                  _UniqueId: groupId,
                  'gift-message': giftMessage,
                },
                callbacks: {
                  beforeFetch: () => drawer.classList.add('loading'),
                  afterFetch: () => {
                    drawer.classList.remove('loading');
                    drawer.classList.remove('open');
                    updateMinicart();
                  },
                },
              });
            }
          });
        }
      }
    });
  }
};

/**
 * Opens the minicart, optionally loading its content before opening.
 *
 * @param {boolean} [loadBeforeOpen=false] - Whether to load the minicart content before opening it.
 */
const openMinicart = async (loadBeforeOpen = false) => {
  if (loadBeforeOpen) await updateMinicart();
  openModal(findMinicart());
};

/**
 * Closes the minicart.
 */
const closeMinicart = () => closeModal(findMinicart());

/**
 * Handler function for cart update events
 * Defined at module scope to allow proper cleanup
 */
const handleMinicartUpdate = () => {
  const minicart = findMinicart();

  // Only update if minicart exists and is not already loading
  if (minicart && !minicart.classList.contains('loading')) {
    updateMinicart();
  }
};

/**
 * Initializes global cart event listener
 * Uses flag to prevent multiple attachments
 */
const initCartEventListener = () => {
  // Prevent multiple listener attachments
  if (cartEventListenerAttached) {
    console.log('[Minicart] Cart event listener already attached');
    return;
  }

  document.addEventListener(afterCartChangeType, handleMinicartUpdate);
  cartEventListenerAttached = true;

  console.log('[Minicart] Cart event listener initialized');
};

/**
 * Removes the global cart event listener
 * Used for cleanup in dynamic scenarios
 */
const removeCartEventListener = () => {
  if (cartEventListenerAttached) {
    document.removeEventListener(afterCartChangeType, handleMinicartUpdate);
    cartEventListenerAttached = false;
    console.log('[Minicart] Cart event listener removed');
  }
};

// Initialize event listener when module loads
initCartEventListener();

export {
  initMinicart,
  openMinicart,
  closeMinicart,
  initLine,
  initCustomization,
  removeOrphanCustomizations,
  removeCartEventListener,
};
